import { PRESENCE_NAMESPACE } from '@amitable/shared/realtime-namespaces'
import type { PresenceAvatar } from '@/features/workspace-presence/model/presence-avatar'
import { UserAvatar } from '@/graphql/schema'
import { normalizePresenceAvatar } from '@/features/workspace-presence/model/presence-avatar'

export { PRESENCE_NAMESPACE }
export const PRESENCE_HEARTBEAT_MS = 15_000
export const PRESENCE_DISCONNECT_STALE_MS = 30_000
export const fallbackWorkspacePresenceAvatar = UserAvatar.WomanChestnutGreen

export type PresenceStatus = 'online' | 'away' | 'offline'

export type WorkspacePresenceUser = {
  // Presence sockets use GraphQL/web enum values like WOMAN_CHESTNUT_GREEN.
  avatar: PresenceAvatar | null
  documentCollectionId: string | null
  lastSeenAt: string | null
  name: string | null
  recordId: string | null
  route: string | null
  sessionCount: number
  status: PresenceStatus
  userId: string
  username: string
}

export type OnlineWorkspacePresence = WorkspacePresenceUser & {
  isMuted: boolean
}

export type PresenceSnapshotEvent = {
  type: 'presence.snapshot'
  workspaceId: string
  workspaceUsers: Array<WorkspacePresenceUser>
  routeUsers: Array<WorkspacePresenceUser>
  recordUsers: Array<WorkspacePresenceUser>
}

export type PresenceChangedEvent = {
  type: 'presence.changed'
  workspaceId: string
  user: WorkspacePresenceUser
}

export type PresenceUnavailableEvent = {
  type: 'presence.unavailable'
  reason: 'auth_unavailable' | 'redis_unavailable' | 'unknown'
}

export function getCurrentDocumentCollectionId(pathname: string) {
  const match = /^\/document-collection\/([^/?#]+)/.exec(pathname)

  return match?.[1] ? decodeURIComponent(match[1]) : null
}

export function getWorkspacePresenceDisplayName(
  presence: Pick<WorkspacePresenceUser, 'name' | 'username'>,
) {
  return presence.name?.trim() || presence.username
}

export function getWorkspacePresenceAvatar(
  presence: Pick<WorkspacePresenceUser, 'avatar'>,
) {
  return (
    normalizePresenceAvatar(presence.avatar) ?? fallbackWorkspacePresenceAvatar
  )
}

export function normalizeWorkspacePresenceUser(
  presence: WorkspacePresenceUser,
): WorkspacePresenceUser {
  return {
    ...presence,
    avatar: normalizePresenceAvatar(presence.avatar),
  }
}

export function decorateWorkspacePresence({
  currentDocumentCollectionId,
  presenceUsers,
}: {
  currentDocumentCollectionId: string | null
  presenceUsers: Array<WorkspacePresenceUser>
}): Array<OnlineWorkspacePresence> {
  return presenceUsers
    .filter((presence) => presence.status !== 'offline')
    .sort((left, right) =>
      getWorkspacePresenceDisplayName(left).localeCompare(
        getWorkspacePresenceDisplayName(right),
      ),
    )
    .map((presence) => ({
      ...presence,
      isMuted: isMutedPresence({
        currentDocumentCollectionId,
        presence,
      }),
    }))
}

function isMutedPresence({
  currentDocumentCollectionId,
  presence,
}: {
  currentDocumentCollectionId: string | null
  presence: Pick<WorkspacePresenceUser, 'documentCollectionId'>
}) {
  if (!currentDocumentCollectionId || !presence.documentCollectionId) {
    return false
  }

  return presence.documentCollectionId !== currentDocumentCollectionId
}
